import React, { useState, useEffect } from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { IUserModel, IRvcModelAvailable, CreateAudioEditingRequest, ModelType } from '@packages/global';
import { useTranslation } from 'react-i18next';
import { ModelSelector } from '../common/form';
import AudioUpload from '../common/AudioUpload';
import { useCoinStore } from '../../stores/coinStore';
import { rvcModelUserApi } from '../../services/user/rvcModel.api';

export interface AudioEditFormData {
  srcAudioUrl: string;
  modelId: string;
  rvcModelId: string;
  pitch: number;
  indexRate: number;
  f0Method: string;
}

export const DEFAULT_AUDIO_EDIT_FORM: AudioEditFormData = {
  srcAudioUrl: '',
  modelId: '',
  rvcModelId: '',
  pitch: 0,
  indexRate: 0.75,
  f0Method: '',
};

interface Props {
  formData: AudioEditFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onFormDataChange: (data: Partial<AudioEditFormData>) => void;
  onSubmit: (data: CreateAudioEditingRequest) => Promise<void>;
}

export default function AudioEditingForm({ formData, models, modelsLoading, onFormDataChange, onSubmit }: Props) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rvcModels, setRvcModels] = useState<IRvcModelAvailable[]>([]);
  const [rvcModelsLoading, setRvcModelsLoading] = useState(false);

  const audioEditModels = models.filter(m => m.modelType === ModelType.AUDIO_EDIT);
  const selectedModel = audioEditModels.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;
  const hasEnoughCoins = (account?.balance ?? 0) >= modelPrice;

  const F0_METHOD_OPTIONS = [
    { value: '', label: t('generation:audioEdit.form.f0Default') },
    { value: 'pm', label: t('generation:audioEdit.form.f0Pm') },
    { value: 'harvest', label: t('generation:audioEdit.form.f0Harvest') },
    { value: 'crepe', label: t('generation:audioEdit.form.f0Crepe') },
    { value: 'rmvpe', label: t('generation:audioEdit.form.f0Rmvpe') },
  ];

  useEffect(() => {
    if (!modelsLoading && !formData.modelId && audioEditModels.length > 0) {
      onFormDataChange({ modelId: audioEditModels[0].id });
    }
  }, [audioEditModels, modelsLoading, formData.modelId]);

  // 组件挂载时加载可用 RVC 模型
  useEffect(() => {
    setRvcModelsLoading(true);
    rvcModelUserApi.getAvailable()
      .then(list => {
        setRvcModels(list);
        if (list.length > 0 && !formData.rvcModelId) {
          onFormDataChange({ rvcModelId: list[0].applioModelId });
        }
      })
      .catch(() => { })
      .finally(() => setRvcModelsLoading(false));
  }, []);

  const validate = (): string => {
    if (!formData.modelId) return t('generation:audioEdit.form.validationSelectModel');
    if (!formData.srcAudioUrl) return t('generation:audioEdit.form.validationUploadAudio');
    if (!formData.rvcModelId.trim()) return t('generation:audioEdit.form.validationSelectRvc');
    if (!hasEnoughCoins && modelPrice > 0) return t('generation:audioEdit.form.validationInsufficientCoins', { price: modelPrice });
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = validate();
    if (msg) { setError(msg); return; }
    setError('');
    setSubmitting(true);
    try {
      const data: CreateAudioEditingRequest = {
        srcAudioUrl: formData.srcAudioUrl,
        editingType: 'voice_change',
        modelId: formData.modelId,
        rvcModelId: formData.rvcModelId,
        pitch: formData.pitch,
        indexRate: formData.indexRate,
        ...(formData.f0Method && { f0Method: formData.f0Method }),
      };
      await onSubmit(data);
      onFormDataChange({ srcAudioUrl: '' });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !modelsLoading && !!formData.modelId && !!formData.srcAudioUrl && !!formData.rvcModelId.trim() && hasEnoughCoins;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* 模型选择 */}
        <ModelSelector
          models={audioEditModels}
          selectedModelId={formData.modelId}
          onChange={(modelId) => onFormDataChange({ modelId })}
          loading={modelsLoading}
          placeholder={t('generation:audioEdit.form.selectModel')}
        />

        {/* 源音频上传 */}
        <div>
          <p className="text-xs text-gray-500 mb-1.5">{t('generation:audioEdit.form.sourceAudio')}</p>
          <AudioUpload
            value={formData.srcAudioUrl}
            onUploadSuccess={(url) => onFormDataChange({ srcAudioUrl: url })}
            showAudioSelector={true}
            showMusicSelector={false}
            placeholder={t('generation:audioEdit.form.uploadSourceAudio')}
          />
        </div>

        {/* RVC 声音模型 */}
        <div>
          <label className="text-xs text-gray-500 mb-1 block">
            {t('generation:audioEdit.form.rvcModelLabel')} <span className="text-red-400">{t('generation:audioEdit.form.rvcModelRequired')}</span>
          </label>
          {rvcModelsLoading ? (
            <div className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-500">
              {t('generation:audioEdit.form.rvcModelLoading')}
            </div>
          ) : rvcModels.length === 0 ? (
            <div className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-500">
              {t('generation:audioEdit.form.rvcModelEmpty')}
            </div>
          ) : (
            <select
              value={formData.rvcModelId}
              onChange={e => onFormDataChange({ rvcModelId: e.target.value })}
              className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
            >
              <option value="">{t('generation:audioEdit.form.rvcModelPlaceholder')}</option>
              {rvcModels.map(m => (
                <option key={m.id} value={m.applioModelId}>
                  {m.name}{m.description ? ` — ${m.description}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 参数调节 */}
        <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 space-y-4">
          {/* 音调偏移 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-300 font-medium">{t('generation:audioEdit.form.pitchLabel')}</span>
              <span className="text-xs font-mono text-primary">
                {formData.pitch > 0 ? `+${formData.pitch}` : formData.pitch}
              </span>
            </div>
            <input
              type="range" min={-12} max={12} step={1}
              value={formData.pitch}
              onChange={e => onFormDataChange({ pitch: parseInt(e.target.value) })}
              className="w-full accent-primary"
            />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>{t('generation:audioEdit.form.pitchDown')}</span>
              <span>{t('generation:audioEdit.form.pitchCenter')}</span>
              <span>{t('generation:audioEdit.form.pitchUp')}</span>
            </div>
          </div>

          {/* 音色融合度 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-300 font-medium">{t('generation:audioEdit.form.indexRateLabel')}</span>
              <span className="text-xs font-mono text-primary">{formData.indexRate.toFixed(2)}</span>
            </div>
            <input
              type="range" min={0} max={1} step={0.05}
              value={formData.indexRate}
              onChange={e => onFormDataChange({ indexRate: parseFloat(e.target.value) })}
              className="w-full accent-primary"
            />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>{t('generation:audioEdit.form.indexRateOriginal')}</span>
              <span>{t('generation:audioEdit.form.indexRateModel')}</span>
            </div>
          </div>
        </div>

        {/* F0 方法 */}
        <div>
          <label className="text-xs text-gray-500 mb-1 block">{t('generation:audioEdit.form.f0MethodLabel')}</label>
          <select
            value={formData.f0Method}
            onChange={e => onFormDataChange({ f0Method: e.target.value })}
            className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
          >
            {F0_METHOD_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* 错误提示 */}
        {error && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>

      {/* 提交按钮 */}
      <div className="p-6 pt-0 space-y-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
        >
          {submitting ? t('generation:audioEdit.form.submitting') : t('generation:audioEdit.form.submitButton')}
        </button>
        {!modelsLoading && modelPrice > 0 && (
          <div className="flex items-center justify-between text-xs px-1">
            <div className="flex items-center gap-1 text-gray-500">
              <CurrencyDollarIcon className="w-3.5 h-3.5" />
              <span>{t('generation:audioEdit.form.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">{t('generation:audioEdit.form.balance')}</span>
              <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-red-400'}`}>
                {account?.balance ?? 0}
              </span>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
